<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useTemplateRef } from 'vue'
import { faArrowDownToLine, faImage, faTrash } from '@fortawesome/duotone-regular-svg-icons'
import { AuToast } from '~/stores/toasts'
import { PendoFeature } from '~/models/Pendo'
import useImageUpload from '~/composables/useImageUpload'
import { useAppColor } from '~/composables/useAppColor'
import Tooltip from '~/components/ui/Tooltip.vue'
import Button from '~/components/ui/Button.vue'
import AuProgress from '~/components/ui/AuProgress.vue'

const { canEdit, uploadDisabled = false } = defineProps<{
  currentImage?: string
  canEdit?: boolean
  uploadDisabled?: boolean
}>()

const emits = defineEmits<{
  uploadImage: [src: string]
  removeImage: []
}>()

const { t } = useI18n()
const { uploadImage, progress, imageSrc, reset, downloadImage } = useImageUpload()
const { themeColor } = useAppColor()

const fileInput = useTemplateRef<HTMLInputElement>('fileInput')

const clearImageAndFile = () => {
  reset()
  emits('removeImage')
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

const onChange = async (event: Event) => {
  if (!canEdit) {
    AuToast.error(t('formWriter.errorNotAllowed'))
    return
  }

  try {
    await uploadImage.mutateAsync(event)
    if (uploadImage.isSuccess.value && imageSrc.value) {
      emits('uploadImage', imageSrc.value)
    }
  } catch {
    clearImageAndFile()
  }
}
</script>

<template>
  <div class="rounded-xl bg-(--theme-5) p-4">
    <div v-if="!currentImage" class="flex w-full flex-col rounded-xl">
      <div v-if="uploadDisabled" class="flex items-center gap-4 rounded-lg p-4">
        <div class="flex size-[4em] items-center justify-center rounded-full bg-(--theme-10) text-(--theme-50) transition-colors group-hover:bg-(--theme-5)">
          <font-awesome-icon
            :icon="faImage"
            size="2xl"
          />
        </div>
        {{ t('formWriter.chooseImage') }}
      </div>
      <button
        v-else-if="!progress"
        class="group flex grow items-center gap-4 rounded-xl px-5 py-3 transition-colors hover:bg-(--theme-10) focus-visible:ring"
        :class="{ disabled: !canEdit }"
        :disabled="!canEdit"
        @click="fileInput?.click()"
      >
        <div class="flex size-[4em] items-center justify-center rounded-full bg-(--theme-10) text-(--theme-50) transition-colors group-hover:bg-(--theme-5)">
          <font-awesome-icon
            :icon="faImage"
            size="2xl"
          />
        </div>
        <span
          class="text-[1em] font-semibold"
          v-text="t('media.uploadImage')"
        />
      </button>
      <template v-if="canEdit">
        <label
          v-if="!uploadImage.isPending.value"
          class="hidden"
          for="file-upload"
        >
          <span class="sr-only" v-text="t('formWriter.uploadImage')" />
          <input
            id="file-upload"
            ref="fileInput"
            type="file"
            accept="image/*"
            :data-pendo="PendoFeature.FormWriter.UploadImage"
            class="cursor-pointer justify-between rounded-lg bg-(--theme-5) p-2 text-center text-sm ring-2 ring-(--theme-50) transition file:mr-4 file:rounded-full file:border-0 file:bg-(--theme-50) file:px-4 file:py-2 file:text-(--theme-5) hover:bg-(--theme-10) hover:ring hover:file:bg-(--theme-40) focus-visible:ring"
            @change="onChange"
          >
        </label>
        <AuProgress
          v-else
          v-model="progress"
        />
      </template>
    </div>
    <div v-else class="relative">
      <img
        :src="currentImage"
        class="aspect-[3/2] size-full rounded-lg object-cover"
        alt=""
      >
      <div class="absolute right-4 bottom-4 flex gap-4 print:!hidden">
        <Tooltip :content="t('formWriter.downloadImage')">
          <Button
            shape="square"
            size="lg"
            :color="themeColor"
            :aria-label="t('formWriter.downloadImage')"
            @click="downloadImage(currentImage)"
          >
            <font-awesome-icon :icon="faArrowDownToLine" />
          </Button>
        </Tooltip>
        <Tooltip v-if="canEdit" :content="t('formWriter.deleteImage')">
          <Button
            shape="square"
            size="lg"
            :color="themeColor"
            :aria-label="t('formWriter.deleteImage')"
            @click="clearImageAndFile"
          >
            <font-awesome-icon :icon="faTrash" />
          </Button>
        </Tooltip>
      </div>
    </div>
  </div>
</template>

<style scoped>
.disabled {
  opacity: 0.7;
  cursor: not-allowed;
  filter: grayscale(100%);
}
</style>
